import { put, all, call, takeLeading, takeLatest } from 'redux-saga/effects';

import types from './actionTypes';
import * as subscriptionsActions from './actions';

import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import rsf from '../../helpers/firebase';
import { sendEventGA } from '../../lib/analytics';

import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';

const db = getFirestore(rsf.app);
const functions = getFunctions(rsf.app);

const subscriptionTransformer = (subscription, data) => ({
  id: subscription.id,
  ...data,
  amount: data.discountedPrice || data.amount,
  ...(data.nextBillingDate && {
    nextBillingDate: toDateFirebase(
      subscription,
      data,
      'nextBillingDate',
    ).toDate(),
  }),
  ...(data.lastBillingDate && {
    lastBillingDate: toDateFirebase(
      subscription,
      data,
      'lastBillingDate',
    ).toDate(),
  }),
  ...(data.createdAt && {
    createdAt: toDateFirebase(subscription, data).toDate(),
  }),
  ...(data.updatedAt && {
    updatedAt: toDateFirebase(subscription, data, 'updatedAt').toDate(),
  }),
});

function* createSubscriptionSaga({ subscription }) {
  try {
    const subscriptionRef = collection(db, 'subscriptions');

    yield call(rsf.firestore.addDocument, subscriptionRef, {
      ...subscription,
      createdAt: serverTimestamp(),
    });
    yield put(subscriptionsActions.createSubscriptionSuccess(subscription));
    toastr.success('Subscription created!', '');
  } catch (error) {
    yield put(subscriptionsActions.createSubscriptionFailure(error));
  }
}

function* updateSubscriptionSaga({ subscription }) {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscription.id);

    yield call(
      rsf.firestore.setDocument,
      subscriptionRef,
      {
        ...subscription,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    yield put(subscriptionsActions.updateSubscriptionSuccess(subscription));
    toastr.success('Subscription updated!', '');
  } catch (error) {
    yield put(subscriptionsActions.updateSubscriptionFailure(error));
  }
}

function* fetchSubscriptionsSaga({ startDate, endDate }) {
  try {
    const subscriptionsRef = rsf.firestore.createCollectionRefWithFilters(
      'subscriptions',
      startDate,
      endDate,
    );
    const subscriptionsSnap = yield call(
      rsf.firestore.getCollection,
      subscriptionsRef,
    );
    const subscriptions = subscriptionsSnap.docs.map((snap) =>
      subscriptionTransformer(snap, snap.data()),
    );
    yield put(
      subscriptionsActions.fetchSubscriptionsSuccess(
        subscriptions,
        startDate,
        endDate,
      ),
    );
  } catch (error) {
    yield put(subscriptionsActions.fetchSubscriptionsFailure(error));
  }
}

function* fetchSubscriptionsByUserSaga({ userId }) {
  try {
    const subscriptionsRef = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      limit(1),
    );
    const subscriptionsSnap = yield call(
      rsf.firestore.getCollection,
      subscriptionsRef,
    );
    const subscriptions = subscriptionsSnap.docs.map((snap) =>
      subscriptionTransformer(snap, snap.data()),
    );

    yield put(
      subscriptionsActions.fetchSubscriptionsByUserSuccess(subscriptions),
    );
  } catch (error) {
    yield put(subscriptionsActions.fetchSubscriptionsByUserFailure(error));
  }
}

function* cancelSubscriptionSaga({ subscriptionId, userId }) {
  try {
    const cancelSubscriptionFunction = httpsCallable(
      functions,
      'cancelSubscription',
    );
    const { data } = yield call(cancelSubscriptionFunction, {
      subscriptionId,
      userId,
    });

    if (data.error) {
      throw new Error(data.error.message);
    }

    sendEventGA('cancel_subscription', {
      userId,
      subscriptionId,
    });

    yield put(
      subscriptionsActions.cancelSubscriptionSuccess(
        data.message,
        data.subscription,
      ),
    );
  } catch (error) {
    yield put(subscriptionsActions.cancelSubscriptionFailure(error));
  }
}

function* subscriptionSaga() {
  yield all([
    takeLatest(types.FETCH_SUBSCRIPTIONS.REQUEST, fetchSubscriptionsSaga),
    takeLeading(types.CREATE_SUBSCRIPTION.REQUEST, createSubscriptionSaga),
    takeLeading(types.UPDATE_SUBSCRIPTION.REQUEST, updateSubscriptionSaga),
    takeLeading(
      types.FETCH_SUBSCRIPTIONS_BY_USER.REQUEST,
      fetchSubscriptionsByUserSaga,
    ),
    takeLeading(types.CANCEL_SUBSCRIPTION.REQUEST, cancelSubscriptionSaga),
  ]);
}

export default subscriptionSaga;
